import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import PageTransition from "../components/PageTransition";
import BlossomMeter from "../components/BlossomMeter";

interface Question {
  id: number;
  text: string;
  position: number;
  flower_profile: {
    id: number;
    name: string;
    slug: string;
  };
}

interface Answer {
  id: number;
  text: string;
  score_multiplier: number;
}

const QUESTIONS_PER_PAGE = 6;

const Quiz: React.FC = () => {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [answers, setAnswers] = useState<Answer[]>([]);
  const [ratings, setRatings] = useState<Record<number, number>>({}); // question_id → answer_id
  const [currentPage, setCurrentPage] = useState(-1); // -1 = intro screen
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchQuizData();
  }, []);

  const fetchQuizData = async () => {
    try {
      const response = await fetch("/api/quiz");
      if (!response.ok) throw new Error("Failed to load quiz");
      const data = await response.json();
      setQuestions(data.questions);
      setAnswers(data.answers);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load quiz");
    } finally {
      setLoading(false);
    }
  };

  // Group questions into pages of 6
  const pages: Question[][] = [];
  for (let i = 0; i < questions.length; i += QUESTIONS_PER_PAGE) {
    pages.push(questions.slice(i, i + QUESTIONS_PER_PAGE));
  }

  const totalPages = pages.length;
  const currentQuestions = currentPage >= 0 ? pages[currentPage] || [] : [];

  const allCurrentPageRated =
    currentQuestions.length > 0 &&
    currentQuestions.every((q) => ratings[q.id] !== undefined);

  const handleSelect = (questionId: number, answerId: number) => {
    setRatings((prev) => ({ ...prev, [questionId]: answerId }));
  };

  const handleNext = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
      window.scrollTo({ top: 0, behavior: "instant" });
    } else {
      submitQuiz();
    }
  };

  const handleBack = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
      window.scrollTo({ top: 0, behavior: "instant" });
    } else if (currentPage === 0) {
      setCurrentPage(-1);
    }
  };

  const submitQuiz = async () => {
    setSubmitting(true);
    try {
      const responses = Object.entries(ratings).map(
        ([questionId, answerId]) => ({
          question_id: Number(questionId),
          answer_id: answerId,
        })
      );

      const response = await fetch("/api/quiz/calculate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ responses }),
      });

      if (!response.ok) throw new Error("Failed to calculate results");

      const data = await response.json();

      if (data.result_id) {
        navigate(`/results/${data.result_id}`);
      } else {
        setError("Failed to determine result");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to submit quiz");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-brand-50 to-lavender-25 dark:from-gray-900 dark:to-gray-800">
        <p className="text-gray-600 dark:text-gray-400">Loading quiz...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-brand-50 to-lavender-25 dark:from-gray-900 dark:to-gray-800">
        <div className="text-center">
          <p className="text-red-600 dark:text-red-400 mb-4">
            Error: {error}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="text-brand-600 dark:text-brand-400 hover:underline"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-brand-50 to-lavender-25 dark:from-gray-900 dark:to-gray-800">
        <p className="text-gray-600 dark:text-gray-400">
          No questions available
        </p>
      </div>
    );
  }

  if (submitting) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-brand-50 to-lavender-25 dark:from-gray-900 dark:to-gray-800">
        <p className="text-gray-600 dark:text-gray-400">
          Calculating your results...
        </p>
      </div>
    );
  }

  // Intro screen
  if (currentPage === -1) {
    return (
      <PageTransition>
        <div className="min-h-screen bg-gradient-to-b from-brand-50 to-lavender-25 dark:from-gray-900 dark:to-gray-800 py-12">
          <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
            <div className="bg-gradient-to-br from-yellow-25 to-brand-25 dark:bg-gray-800 rounded-2xl shadow-xl p-8 sm:p-12 animate-slide-up">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-6 text-center">
                How to Take the Quiz
              </h1>

              <div className="space-y-4 text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
                <p>Rate each statement as the version that feels most like you.</p>
                <p>Not the rushed version.</p>
                <p>Not the people-pleasing version.</p>
                <p className="font-semibold text-gray-900 dark:text-white">
                  The real you.
                </p>
                <p>Who you'd be if you had time to focus on yourself.</p>
                <p>Feel free to let your bestie weigh in.</p>
              </div>

              <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  Blossom Meter
                </h2>
                <div className="grid grid-cols-5 gap-2 sm:gap-4 text-center text-sm">
                  {[
                    { num: 1, label: "Nope, Not Me" },
                    { num: 2, label: "Sort of Me" },
                    { num: 3, label: "In Between" },
                    { num: 4, label: "Mostly Me" },
                    { num: 5, label: "Absolutely Me" },
                  ].map((item) => (
                    <div key={item.num} className="flex flex-col items-center gap-2">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-brand-100 dark:bg-brand-900/40 text-brand-600 dark:text-brand-400 flex items-center justify-center font-bold text-base sm:text-lg">
                        {item.num}
                      </div>
                      <span className="text-gray-600 dark:text-gray-400 text-xs sm:text-sm leading-tight">
                        {item.label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-8 text-center">
                <p className="text-gray-500 dark:text-gray-400 mb-6">
                  The scoring will get you close. But the real test?{" "}
                  <span className="font-semibold text-gray-900 dark:text-white">
                    Your true blossom will speak for itself.
                  </span>
                </p>
                <button
                  onClick={() => setCurrentPage(0)}
                  className="rounded-md bg-brand-500 px-8 py-4 text-lg font-semibold text-white shadow-lg hover:bg-brand-600 transition-all transform hover:scale-105"
                >
                  Begin the Quiz
                </button>
              </div>
            </div>
          </div>
        </div>
      </PageTransition>
    );
  }

  const progress = (currentPage / totalPages) * 100;

  return (
    <PageTransition>
      <div className="min-h-screen bg-gradient-to-b from-brand-50 to-lavender-25 dark:from-gray-900 dark:to-gray-800 py-12">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          {/* Progress Bar */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Page {currentPage + 1} of {totalPages}
              </span>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {Math.round(progress)}% Complete
              </span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
              <div
                className="bg-brand-500 h-2.5 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
          </div>

          {/* Blossom Meter Legend */}
          <div className="mb-6 grid grid-cols-5 gap-2 sm:gap-4 text-center">
            {[
              { num: 1, label: "Nope, Not Me" },
              { num: 2, label: "Sort of Me" },
              { num: 3, label: "In Between" },
              { num: 4, label: "Mostly Me" },
              { num: 5, label: "Absolutely Me" },
            ].map((item) => (
              <div
                key={item.num}
                className="flex flex-col items-center gap-2"
              >
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-brand-100 dark:bg-brand-900/40 text-brand-600 dark:text-brand-400 flex items-center justify-center font-bold text-base sm:text-lg">
                  {item.num}
                </div>
                <span className="text-gray-600 dark:text-gray-400 text-xs sm:text-sm leading-tight">
                  {item.label}
                </span>
              </div>
            ))}
          </div>

          {/* Statement Cards */}
          <div className="space-y-4">
            {currentQuestions.map((question, index) => (
              <div
                key={question.id}
                className="bg-gradient-to-br from-yellow-25 to-brand-25 dark:bg-gray-800 rounded-xl shadow-md p-5 sm:p-6 animate-slide-up"
                style={{ animationDelay: `${index * 50}ms`, animationFillMode: "both" }}
              >
                <p className="text-base sm:text-lg text-gray-900 dark:text-white mb-4 leading-relaxed">
                  {question.text}
                </p>
                <BlossomMeter
                  questionId={question.id}
                  selectedAnswerId={ratings[question.id]}
                  answers={answers}
                  onSelect={handleSelect}
                />
              </div>
            ))}
          </div>

          {/* Navigation */}
          <div className="mt-8 flex justify-between items-center">
            <button
              onClick={handleBack}
              className="text-brand-600 dark:text-brand-400 hover:text-brand-500 font-medium"
            >
              &larr; Back
            </button>

            <button
              onClick={handleNext}
              disabled={!allCurrentPageRated}
              className={`
                rounded-md px-8 py-3 text-base font-semibold shadow-md transition-all
                ${
                  allCurrentPageRated
                    ? "bg-brand-500 text-white hover:bg-brand-600 transform hover:scale-105"
                    : "bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                }
              `}
            >
              {currentPage === totalPages - 1
                ? "See My Results"
                : "Next Page"}
            </button>
          </div>
        </div>
      </div>
    </PageTransition>
  );
};

export default Quiz;
